import { z } from 'zod';
import { OptomechAssemblyHoldSchema, OptomechDatumSchema } from './optomech.js';
import { OPTOMECH_GENERATED_FAMILIES, OptomechAnnulusSchema, OptomechPartFamilySchema, OptomechPartRefSchema, type OptomechPartRef } from './optomechParts.js';
import { SEMVER_RE } from './semver.js';

/**
 * Compositions — spec docs/superpowers/specs/2026-10-19-optomech-catalog-composition-design.md
 * §4.6, §6.1, §6.2 (amended 2026-10-20).
 *
 * A composition is the assembly model the layout rule produced around a lens
 * of the design: catalog parts placed along the optical axis, the joints
 * between neighbours, the hold, and the rule's inputs and facts. Positions are
 * in the run's frame (surface 1's vertex at z = 0, light toward +z); every
 * part's own datum is its rear face, so `axial_mm` is where that face lands.
 */

/** The one layout rule of the first releases: an ordered stack along the axis inside one tube. */
export const OPTOMECH_LAYOUT_RULE = 'axial_stack';
/**
 * The rule's version, carried beside its name the way the mental-model
 * detectors carry DETECTOR_VERSION: semver, the major bumped when an existing
 * stack would be placed differently, the minor for a new item kind or a new
 * fact, the patch for a message. A composition records the pair.
 *
 * 2.0.0 (general-stack design 2026-10-28 §3.1–§3.4): the walk places any
 * number of elements, each a singlet or a cemented group read from the
 * prescription; joints name BODY faces, seats carry a type, cement faces are
 * joints, and the facts are named per element and per spacer. The presets
 * are placed exactly where 1.1.0 placed them.
 */
export const OPTOMECH_LAYOUT_RULE_VERSION = '2.0.0';

/** Named example stacks the agent and the UI can start from; each expands to a `stack` the rule walks like any other. */
export const OPTOMECH_LAYOUT_PRESETS = ['lens_on_ring_in_tube', 'lens_on_shoulder_in_tube', 'two_lenses_spacer'] as const;
export const OptomechLayoutPresetSchema = z.enum(OPTOMECH_LAYOUT_PRESETS);
export type OptomechLayoutPreset = z.infer<typeof OptomechLayoutPresetSchema>;

/**
 * One item of the stack, front to rear: a ring, then the elements with a
 * spacer between each two, then a ring or the tube's shoulder. A `shoulder`
 * is the tube's own stop, not a part; a `spacer` rides in the bore between
 * two elements and must realize the prescription's air gap between them.
 */
export const OptomechStackItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ring'), part_id: z.string().uuid() }).strict(),
  /** The ELEMENT whose first surface this is: a singlet, or a cemented group of several bodies (elements.ts elementsFromSystem). */
  z.object({ kind: z.literal('lens'), front_surface: z.number().int().positive() }).strict(),
  z.object({ kind: z.literal('spacer'), part_id: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('shoulder') }).strict(),
]);
export type OptomechStackItem = z.infer<typeof OptomechStackItemSchema>;

/**
 * The user's numbers. None has a default (spec §11 Q3): a missing one is a
 * refusal by name, never a value nobody stated. The clearance is DIAMETRAL.
 */
export const OptomechLayoutInputsSchema = z.object({
  diametral_clearance_mm: z.number().finite().nonnegative(),
  /** Width of the seat joint's band on the lens face, inboard from the seat radius. */
  seat_band_mm: z.number().finite().positive(),
  preload_force_n: z.number().finite().positive(),
}).strict();
export type OptomechLayoutInputs = z.infer<typeof OptomechLayoutInputsSchema>;

/** Body of POST /optomech/models. */
export const OptomechComposeRequestSchema = z.object({
  conversation_id: z.string().uuid(),
  tube_part_id: z.string().uuid(),
  stack: z.array(OptomechStackItemSchema).min(2),
  inputs: OptomechLayoutInputsSchema,
  /** Recorded when the stack came from a preset; the stack itself is what the rule walks. */
  preset: OptomechLayoutPresetSchema.optional(),
}).strict();
export type OptomechComposeRequest = z.infer<typeof OptomechComposeRequestSchema>;

export const OptomechPlacementSchema = z.object({
  /** Where the part's rear face lands, from surface 1's vertex along +z. */
  axial_mm: z.number(),
  roll_deg: z.number(),
  /** Half-turn about x before placing: the part's front face then faces +z. */
  flip: z.boolean(),
}).strict();

/**
 * One lens body of an element: the glass between two consecutive surfaces.
 * `body_id` is `<element_id>.<n>`, n counting from 1 at the front.
 */
export const OptomechElementBodySchema = z.object({
  body_id: z.string().regex(/^[^.]+\.[1-9]\d*$/),
  front: z.number().int(),
  rear: z.number().int(),
  /** The catalog glass of this body, or null for a model glass; the run resolves it to a material. */
  glass: z.string().nullable(),
}).strict();
export type OptomechElementBody = z.infer<typeof OptomechElementBodySchema>;

/** The element's mechanical outline. No value has a default (general-stack design §3.1). */
export const OptomechElementOutlineSchema = z.object({
  /** The edge (mechanical) diameter every body of the element shares. */
  edge_diameter_mm: z.number().positive(),
}).strict();

/**
 * An element as a row of optomech_models STORES it, whatever rule version
 * wrote it — never served. Rule 2.0.0 rows carry `bodies` and `outline` (and,
 * written between 2026-10-29 and the field's removal, a `glass` string that
 * duplicated them); a rule-1.x row carries `surfaces` [a, b] and `glass` only.
 * `readModelElements` turns either into the `OptomechElement` the API serves.
 */
export const OptomechStoredModelElementSchema = z.object({
  element_id: z.string().min(1),
  surfaces: z.array(z.number().int()).min(2),
  bodies: z.array(OptomechElementBodySchema).min(1).optional(),
  outline: OptomechElementOutlineSchema.optional(),
  /** A rule-1.x element's one glass; on a 2.0.0 row a copy of the bodies', ignored on read. */
  glass: z.string().nullable().optional(),
}).strict();
export type OptomechStoredModelElement = z.infer<typeof OptomechStoredModelElementSchema>;

/**
 * An element as the API serves it and every reader takes it (general-stack
 * design §3.1): a maximal run of surfaces with glass between them, bounded by
 * air; k + 1 surfaces hold k bodies, the inner surfaces cement faces.
 */
export const OptomechElementSchema = z.object({
  // `min(1)` and no `pattern`: the served `element_id` keeps the JSON Schema it had (the contract gate blocks a
  // dropped `minLength`); the no-dot rule is the refinement below, which reading every row enforces.
  element_id: z.string().min(1),
  surfaces: z.array(z.number().int()).min(2),
  bodies: z.array(OptomechElementBodySchema).min(1),
  outline: OptomechElementOutlineSchema,
}).strict().superRefine((e, ctx) => {
  if (e.element_id.includes('.')) {
    ctx.addIssue({ code: 'custom', message: `element id ${e.element_id} holds a dot; a body id is <element_id>.<n>, so an element id cannot` });
  }
  if (!e.surfaces.every((s, i) => i === 0 || s === e.surfaces[i - 1] + 1)) {
    ctx.addIssue({ code: 'custom', message: `element ${e.element_id}'s surfaces ${e.surfaces.join(', ')} are not consecutive and ascending` });
  }
  if (e.bodies.length !== e.surfaces.length - 1) {
    ctx.addIssue({ code: 'custom', message: `element ${e.element_id} has ${e.surfaces.length} surfaces and ${e.bodies.length} bodies; k + 1 surfaces hold k bodies` });
  }
  e.bodies.forEach((b, i) => {
    if (b.body_id !== `${e.element_id}.${i + 1}` || b.front !== e.surfaces[i] || b.rear !== e.surfaces[i + 1]) {
      ctx.addIssue({ code: 'custom', message: `body ${i + 1} of element ${e.element_id} must be ${e.element_id}.${i + 1} on surfaces ${e.surfaces[i]}–${e.surfaces[i + 1]}, not ${b.body_id} on ${b.front}–${b.rear}` });
    }
  });
});
export type OptomechElement = z.infer<typeof OptomechElementSchema>;

/** An element's first and last surface: its front and rear faces, whatever the cement faces between them. */
export const elementSpan = (e: { surfaces: readonly number[] }): { front: number; rear: number } => ({ front: e.surfaces[0], rear: e.surfaces[e.surfaces.length - 1] });

/** An element as every reader names it: `surfaces <front>–<rear>`. */
export const elementLabel = (e: { surfaces: readonly number[] }): string => {
  const { front, rear } = elementSpan(e);
  return `surfaces ${front}–${rear}`;
};

/** Which lens face a placed part bears on: a ring in front of the stack or behind it; null for a tube or a spacer. */
export const OptomechPartSideSchema = z.enum(['front', 'rear']).nullable();
export type OptomechPartSide = z.infer<typeof OptomechPartSideSchema>;

/**
 * A placed part as the layout rule writes it and a row of optomech_models
 * stores it: the part by reference, its family, its side and its placement.
 * `side` is absent on rows stored before it was written; `readStoredModel`
 * reads those from the rule's own instance naming.
 */
export const OptomechStoredModelPartSchema = z.object({
  instance: z.string().min(1),
  part: OptomechPartRefSchema,
  family: OptomechPartFamilySchema,
  side: OptomechPartSideSchema.optional(),
  placement: OptomechPlacementSchema,
}).strict();
export type OptomechStoredModelPart = z.infer<typeof OptomechStoredModelPartSchema>;

/**
 * A placed part as the API serves it: the stored placement with the part's
 * identity (vendor, part number, name) read off its catalog row at read time —
 * the row is the one source of what a part is called, and nothing here can
 * disagree with it.
 */
export const OptomechModelPartSchema = OptomechStoredModelPartSchema.extend({
  side: OptomechPartSideSchema,
  vendor: z.string(),
  part_number: z.string(),
  name: z.string(),
}).strict();
export type OptomechModelPart = z.infer<typeof OptomechModelPartSchema>;

/**
 * How a carrier meets a lens face (general-stack design §3.3). Rule 2.0.0
 * writes only `flat`: a flat carrier face bearing on a convex lens face at the
 * carrier's inner edge (whether the build lips it is fea/build.py's rule), or
 * on a flat lens face over the annulus the two faces overlap, from that edge
 * to the nearer of the lens edge and the carrier's rim, the joint's band then
 * covering that annulus.

 */
export const OPTOMECH_SEAT_TYPES = ['flat'] as const;
export const OptomechSeatTypeSchema = z.enum(OPTOMECH_SEAT_TYPES);
export type OptomechSeatType = z.infer<typeof OptomechSeatTypeSchema>;

/**
 * Joints between neighbours (spec §6.1): what each becomes in the mesh is the
 * run's business. A lens face is `<body_id>.S<n>` (rule 2.0.0) or
 * `<element_id>.S<n>` (rule 1.x, whose elements are one body each); read
 * either with `parseBodyFace`.
 */
export const OptomechModelJointSchema = z.discriminatedUnion('kind', [
  /** Coincident faces: a lens body face on a ring's seat face, on a spacer's front or rear face, or on the tube's shoulder, over a band at the seat radius. */
  z.object({
    kind: z.literal('seat'), a: z.string().min(1), b: z.string().min(1), radius_mm: z.number().positive(), band_mm: z.number().positive(),
    /** Absent only on a rule-1.x row, every seat of which was a flat carrier face. */
    seat_type: OptomechSeatTypeSchema.optional(),
  }).strict(),
  /** Coincident cylinders at the thread interface diameter. */
  z.object({ kind: z.literal('thread'), a: z.string().min(1), b: z.string().min(1) }).strict(),
  /** The named ring presses the named lens body face with a force, over a band at the seat radius. */
  z.object({ kind: z.literal('preload'), ring: z.string().min(1), face: z.string().min(1), force_n: z.number().positive(), radius_mm: z.number().positive(), band_mm: z.number().positive() }).strict(),
  /**
   * Two consecutive bodies `a`, `b` of one element share the cement face
   * `<element_id>.S<n>` (general-stack design §3.4): a perfect bond, the
   * cement layer not modelled.
   */
  z.object({ kind: z.literal('cement'), face: z.string().min(1), a: z.string().min(1), b: z.string().min(1) }).strict(),
]);
export type OptomechModelJoint = z.infer<typeof OptomechModelJointSchema>;

export const OptomechLayoutFactsSchema = z.record(z.string(), z.union([z.number(), z.string(), z.boolean()]));

/** The §6.1 document. */
export const OptomechAssemblyModelSchema = z.object({
  system_ref: z.object({ system_id: z.string().uuid(), version: z.number().int().positive() }).strict(),
  elements: z.array(OptomechElementSchema).min(1),
  parts: z.array(OptomechModelPartSchema).min(1),
  joints: z.array(OptomechModelJointSchema),
  holds: z.array(OptomechAssemblyHoldSchema).min(1),
  layout: z.object({
    rule: z.literal(OPTOMECH_LAYOUT_RULE),
    rule_version: z.string().regex(SEMVER_RE),
    preset: OptomechLayoutPresetSchema.nullable(),
    stack: z.array(OptomechStackItemSchema),
    inputs: OptomechLayoutInputsSchema,
    /** Every derived number the placements rest on, named: seat radii, thicknesses at the seats, the stack length, margins. */
    facts: OptomechLayoutFactsSchema,
  }).strict(),
}).strict();
export type OptomechAssemblyModel = z.infer<typeof OptomechAssemblyModelSchema>;

/** The model as the layout rule writes it: the current rule's elements, its parts by reference (their identity joins on read). */
export type OptomechLayoutModel = Omit<OptomechAssemblyModel, 'parts'> & { parts: OptomechStoredModelPart[] };

/** The model as a row of optomech_models stores it: its elements in any rule version's shape, its parts by reference without their identity. */
export type OptomechStoredAssemblyModel = Omit<OptomechAssemblyModel, 'elements' | 'parts'> & { elements: OptomechStoredModelElement[]; parts: OptomechStoredModelPart[] };

/** The identity a model part is served with, read off its catalog row. */
export type OptomechPartIdentity = Pick<OptomechModelPart, 'vendor' | 'part_number' | 'name'>;

/**
 * The stored parts as the API serves them: each joined with its identity (the
 * caller reads the rows), and a row stored before `side` was written read from
 * the layout rule's own instance naming of that time (`ring_front`, `ring_rear`
 * in layout.ts; a tube or a spacer has no side).
 */
export function readModelParts(parts: OptomechStoredModelPart[], identityOf: (ref: OptomechPartRef) => OptomechPartIdentity): OptomechModelPart[] {
  return parts.map((p) => ({
    ...p,
    side: p.side === undefined ? (/_(front|rear)$/.exec(p.instance)?.[1] as 'front' | 'rear' | undefined) ?? null : p.side,
    ...identityOf(p.part),
  }));
}

/**
 * The model's elements in the rule-2.0.0 shape, whatever rule wrote the row.
 * A rule-1.x element is one body `<element_id>.1` on its two surfaces, and its
 * edge diameter is the one its layout recorded: `lens_diameter_mm` for L1,
 * `lens2_diameter_mm` for L2, each twice the semi-diameter the singlet was
 * laid out at. A 1.x row missing that fact is refused by name, never guessed.
 * A 2.0.0 row's stored `glass` duplicated its bodies' and is dropped.
 */
export function readModelElements(model: OptomechStoredAssemblyModel): OptomechElement[] {
  return model.elements.map((e) => {
    if (e.bodies !== undefined && e.outline !== undefined) {
      return OptomechElementSchema.parse({ element_id: e.element_id, surfaces: e.surfaces, bodies: e.bodies, outline: e.outline });
    }
    const key = e.element_id === 'L1' ? 'lens_diameter_mm' : e.element_id === 'L2' ? 'lens2_diameter_mm' : null;
    const diameter = key === null ? undefined : model.layout.facts[key];
    if (typeof diameter !== 'number') {
      throw new Error(`element ${e.element_id} of a rule ${model.layout.rule_version} composition carries no bodies or outline, and its layout records no edge diameter for it${key === null ? '' : ` (${key})`}`);
    }
    return OptomechElementSchema.parse({
      element_id: e.element_id,
      surfaces: e.surfaces,
      bodies: [{ body_id: `${e.element_id}.1`, front: e.surfaces[0], rear: e.surfaces[1], glass: e.glass ?? null }],
      outline: { edge_diameter_mm: diameter },
    });
  });
}

/** A stored model as the API serves it: every element read into the rule-2.0.0 shape (`readModelElements`), every part with its identity (`readModelParts`). */
export function readStoredModel(model: OptomechStoredAssemblyModel, identityOf: (ref: OptomechPartRef) => OptomechPartIdentity): OptomechAssemblyModel {
  return { ...model, elements: readModelElements(model), parts: readModelParts(model.parts, identityOf) };
}

/**
 * A joint's lens face, `<body_id>.S<n>`, as the body and the surface number.

 * A rule-1.x face `<element_id>.S<n>` names its element's one body,
 * `<element_id>.1`. Element ids hold no dot, so the two spellings differ by
 * whether the part before `.S` ends in `.<n>`.
 */
export function parseBodyFace(face: string): { body_id: string; surface: number } {
  const m = /^([^.]+)(\.[1-9]\d*)?\.S(\d+)$/.exec(face);
  if (!m) throw new Error(`${face} is not a lens face (<body_id>.S<n>)`);
  return { body_id: m[2] === undefined ? `${m[1]}.1` : `${m[1]}${m[2]}`, surface: Number(m[3]) };
}

/** The faces a carrier seats a lens on, as fea/parts.py SEAT_FACES spells them: a seat joint's `b` is `<instance>.<face>`. */
export const OPTOMECH_CARRIER_FACES = ['seat', 'rear', 'front', 'shoulder'] as const;
export type OptomechCarrierFace = typeof OPTOMECH_CARRIER_FACES[number];

/** A seat joint's carrier face, `<instance>.<face>`, as the carrier instance and the face it bears with. */
export function parseCarrierFace(ref: string): { instance: string; face: OptomechCarrierFace } {
  const m = /^([^.]+)\.(seat|rear|front|shoulder)$/.exec(ref);
  if (!m) throw new Error(`${ref} is not a carrier face (<instance>.<${OPTOMECH_CARRIER_FACES.join('|')}>)`);
  return { instance: m[1], face: m[2] as OptomechCarrierFace };
}

/** Per placed part, what a run will stamp (spec §7). */
export const OptomechPartUsedSchema = z.object({
  instance: z.string().min(1),
  part_id: z.string().uuid(),
  version: z.number().int().positive(),
  /** The row's `geometry.generator` and `geometry.generator_version` at placement.
   *  `generator` stays the non-empty string it was served as (the family enum lives
   *  on the geometry schema): narrowing it here drops `minLength`, which the contract
   *  gate reads as a breaking change. */
  generator: z.string().min(1),
  generator_version: z.string().regex(SEMVER_RE),
}).strict();
export type OptomechPartUsed = z.infer<typeof OptomechPartUsedSchema>;

/** One row of optomech_models as the API serves it. */
export const OptomechModelRowSchema = z.object({
  id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  system_id: z.string().uuid(),
  system_version: z.number().int().positive(),
  model: OptomechAssemblyModelSchema,
  parts_used: z.array(OptomechPartUsedSchema),
  /** Null until the composition is built and its STEP retained. */
  retained_geometry_ref: z.string().nullable(),
  /** The system version the build wrote (carrying the retained ref, the datum and the composition's provenance); null until built. */
  built_system_version: z.number().int().positive().nullable(),
  /** True while the conversation's current design is the version the stack was laid out on or the version its own
   *  build wrote (`built_system_version`: the same optics plus the retained file). False once the design moved on;
   *  the composition is served as it was, never rebuilt silently. */
  system_is_current: z.boolean(),
  created_by: z.string(),
  created_at: z.string(),
});
export type OptomechModelRow = z.infer<typeof OptomechModelRowSchema>;

type BuildFields = Pick<OptomechModelRow, 'retained_geometry_ref' | 'built_system_version'>;
/** The composition has been built: its STEP is retained and the build wrote a system version (the two are set together). */
export const isBuilt = <R extends BuildFields>(row: R): row is R & { retained_geometry_ref: string; built_system_version: number } =>
  row.retained_geometry_ref !== null;

/** What a composition's one-line summary says: its parts, each element with its span and how many bodies it has, the stack and its front margin. */
export function compositionSummaryFacts(row: Pick<OptomechModelRow, 'id' | 'model'>) {
  const f = row.model.layout.facts;
  return {
    id: row.id.slice(0, 8),
    parts: row.model.parts.map((p) => p.instance),
    elements: readModelElements(row.model).map((e) => ({ element_id: e.element_id, ...elementSpan(e), bodies: e.bodies.length })),
    stack_mm: f.stack_length_mm,
    front_margin_mm: f.front_margin_mm,
  };
}

/** A composition in one line, as the agent reports it (optomechCompose); the composer renders the same facts in the user's language. */
export function compositionSummary(row: Pick<OptomechModelRow, 'id' | 'model'>): string {
  const s = compositionSummaryFacts(row);
  const elements = s.elements.map((e) => `${e.element_id} (surfaces ${e.front}–${e.rear}, ${e.bodies === 1 ? 'singlet' : `cemented group of ${e.bodies}`})`);
  return `composition ${s.id}: ${s.parts.join(', ')} around ${elements.join(', ')}, stack ${s.stack_mm} mm, front margin ${s.front_margin_mm} mm`;
}

export const OptomechModelListSchema = z.object({ models: z.array(OptomechModelRowSchema) });

// --- build (spec §7) -----------------------------------------------------------

/** Body of POST /optomech/models/{modelId}/build. */
export const OptomechBuildRequestSchema = z.object({
  conversation_id: z.string().uuid(),
}).strict();
export type OptomechBuildRequest = z.infer<typeof OptomechBuildRequestSchema>;

/**
 * What a built composition writes under `provenance.optomech_composition` on
 * the new system version. The runtime readers of a retained geometry's
 * bodies (the assembly run's resolver, the body list, the Optomech tab) take
 * these two lists from here exactly as they take them from an import's
 * `provenance.cad_import`; the importer's own findings (fits, scores, axis
 * recovery) have no honest value for a generated file and are not written.
 */
/** One lens body of a built element: one glass between two faces, one solid of the STEP (general-stack design §3.1). */
export const OptomechCompositionBodySchema = z.object({
  body_id: z.string().min(1),
  occurrence_path: z.string().min(1),
  /** The body's front and rear surface; a cement face is the rear of one body and the front of the next. */
  surfaces: z.tuple([z.number().int(), z.number().int()]),
  center_thickness_mm: z.number().positive(),
  /** The catalog glass the prescription names on the front surface, or null for a model glass. */
  glass: z.string().nullable(),
}).strict();
export type OptomechCompositionBody = z.infer<typeof OptomechCompositionBodySchema>;

/**
 * A built element: its k + 1 surfaces and its k bodies, one solid each. A build before rule 2.0.0 wrote one
 * solid per element with no `bodies` (the second form): it reads as one body (provenance.ts).
 */
export const OptomechCompositionElementSchema = z.union([
  z.object({
    element_id: z.string().min(1),
    name: z.string().min(1),
    surfaces: z.array(z.number().int()).min(2),
    edge_diameter_mm: z.number().positive(),
    bodies: z.array(OptomechCompositionBodySchema).min(1),
  }).strict(),
  z.object({
    element_id: z.string().min(1),
    name: z.string().min(1),
    occurrence_path: z.string().min(1),
    surfaces: z.array(z.number().int()).min(1),
    center_thickness_mm: z.number().positive(),
  }).strict(),
]);
export type OptomechCompositionElement = z.infer<typeof OptomechCompositionElementSchema>;

export const OptomechCompositionSolidSchema = z.object({
  name: z.string().min(1),
  occurrence_path: z.string().min(1),
  is_solid: z.literal(true),
  bbox_mm: z.array(z.number()).length(6),
  role: z.literal('mount'),
  /** The placed catalog part this solid came from. */
  instance: z.string().min(1),
  part_id: z.string().uuid(),
  version: z.number().int().positive(),
  body_name: z.string().min(1),
}).strict();

export const OptomechCompositionProvenanceSchema = z.object({
  model_id: z.string().uuid(),
  /** The design version the stack was laid out around (the build writes the next one). */
  laid_out_on_version: z.number().int().positive(),
  file_name: z.string().min(1),
  built_at: z.string(),
  elements: z.array(OptomechCompositionElementSchema).min(1),
  non_element_solids: z.array(OptomechCompositionSolidSchema).min(1),
  parts_used: z.array(OptomechPartUsedSchema),
}).strict();
export type OptomechCompositionProvenance = z.infer<typeof OptomechCompositionProvenanceSchema>;

// --- geometry for the viewers ------------------------------------------------------

/** One placed catalog part as the 2D and 3D viewers draw it (its annuli: OptomechAnnulusSchema, optomechParts.ts). */
export const OptomechAssemblyGeometryPartSchema = z.object({
  instance: z.string().min(1),
  family: z.enum(OPTOMECH_GENERATED_FAMILIES),
  part_id: z.string().uuid(),
  version: z.number().int().positive(),
  vendor: z.string(),
  part_number: z.string(),
  name: z.string(),
  side: OptomechPartSideSchema,
  annuli: z.array(OptomechAnnulusSchema).min(1),
}).strict();
export type OptomechAssemblyGeometryPart = z.infer<typeof OptomechAssemblyGeometryPartSchema>;

/**
 * One composed lens BODY as the build makes it (fea/parts.py Lens: a cylinder
 * of the element's edge radius cut by its two faces), with its front vertex at
 * `axial_mm` in the assembly frame — the first element at 0, each body of an
 * element past the bodies before it, the next element past the prescription's
 * air gap, exactly where the layout rule and the build place it. A cemented
 * element is one entry per body, sharing `element_id`. A radius is null for a
 * flat face. What the 3D assembly view revolves.
 */
export const OptomechAssemblyLensSchema = z.object({
  element_id: z.string().min(1),
  body_id: z.string().min(1),
  surfaces: z.tuple([z.number().int(), z.number().int()]),
  r1_mm: z.number().nullable(),
  r2_mm: z.number().nullable(),
  thickness_mm: z.number().positive(),
  semi_diameter_mm: z.number().positive(),
  axial_mm: z.number(),
}).strict();
export type OptomechAssemblyLens = z.infer<typeof OptomechAssemblyLensSchema>;

/**
 * A composition as geometry: by default the one that applies to the
 * conversation's current design (the newest one laid out on, or built onto,
 * the current version), or the one named by `model_id`. `datum` names the
 * lens whose front vertex is the frame's origin, so a viewer drawing in
 * another frame (optiland's, surface 1 at z = 0) shifts by that surface's
 * position.
 */
export const OptomechAssemblyGeometrySchema = z.object({
  model_id: z.string().uuid(),
  /** The design version the stack was laid out around. */
  system_version: z.number().int().positive(),
  built: z.boolean(),
  /** False once the design moved on from the version the stack was laid out on (or its build wrote). */
  system_is_current: z.boolean(),
  datum: z.object({ element_id: z.string().min(1), front_surface: z.number().int() }).strict(),
  lenses: z.array(OptomechAssemblyLensSchema).min(1),
  parts: z.array(OptomechAssemblyGeometryPartSchema).min(1),
}).strict();
export type OptomechAssemblyGeometry = z.infer<typeof OptomechAssemblyGeometrySchema>;

/** Response of GET /optomech/geometry: null when no composition applies to the current design (or the named one is not the conversation's). */
export const OptomechAssemblyGeometryResponseSchema = z.object({ geometry: OptomechAssemblyGeometrySchema.nullable() });
export type OptomechAssemblyGeometryResponse = z.infer<typeof OptomechAssemblyGeometryResponseSchema>;

// --- a retained STEP as meshes for the viewers -----------------------------------

/**
 * One body of the design's retained CAD file as triangles, in the system frame
 * (surface 1's vertex at the origin, light toward +z): the file's world
 * coordinates moved through the import's datum. `kind` is the import report's
 * own classification — an element body is 'lens', a non-element solid with
 * role 'mount' is 'mount'; other solids (image planes, unused optical
 * candidates) are not served.
 */
export const OptomechRetainedMeshBodySchema = z.object({
  occurrence_path: z.string().min(1),
  name: z.string().min(1),
  kind: z.enum(['lens', 'mount']),
  /** x, y, z per vertex, mm. */
  positions: z.array(z.number()),
  /** Three vertex indices per triangle, wound outward. */
  indices: z.array(z.number().int().nonnegative()),
}).strict();
export type OptomechRetainedMeshBody = z.infer<typeof OptomechRetainedMeshBodySchema>;

export const OptomechRetainedMeshSchema = z.object({
  /** The retained file's name in the project file library. */
  file_name: z.string().min(1),
  bodies: z.array(OptomechRetainedMeshBodySchema),
}).strict();
export type OptomechRetainedMesh = z.infer<typeof OptomechRetainedMeshSchema>;

/** Response of GET /optomech/retained-mesh: null when the current design keeps no CAD file. */
export const OptomechRetainedMeshResponseSchema = z.object({ mesh: OptomechRetainedMeshSchema.nullable() });
export type OptomechRetainedMeshResponse = z.infer<typeof OptomechRetainedMeshResponseSchema>;

// --- the bodies the viewers draw -------------------------------------------------

/**
 * One body's solid, whatever it came from. Every shape is in the DATUM frame
 * (`OptomechViewBodiesSchema.datum`): the vertex of the datum surface at the
 * origin, light toward +z, the axis along z.
 *
 * - `annuli`: a solid of revolution about the axis, exactly — a generated
 *   catalog part (every family in fea/parts.py is a stack of coaxial
 *   cylinders, so nothing is approximated).
 * - `singlet`: one composed lens body as the build makes it (fea/parts.py
 *   Lens: a cylinder of the edge radius cut by its two faces, a null radius
 *   being a flat face), its front vertex at `z0_mm`. A cemented element is
 *   one such body per glass.
 * - `mesh`: triangles of a body of the retained file, moved into the frame;
 *   a file body is not a solid of revolution in general, so this is the only
 *   shape that holds every object.
 */
export const OptomechViewShapeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('annuli'), annuli: z.array(OptomechAnnulusSchema).min(1) }).strict(),
  z.object({
    kind: z.literal('singlet'),
    r1_mm: z.number().nullable(),
    r2_mm: z.number().nullable(),
    thickness_mm: z.number().positive(),
    semi_diameter_mm: z.number().positive(),
    z0_mm: z.number(),
  }).strict(),
  z.object({
    kind: z.literal('mesh'),
    /** x, y, z per vertex, mm. */
    positions: z.array(z.number()),
    /** Three vertex indices per triangle, wound outward. */
    indices: z.array(z.number().int().nonnegative()),
  }).strict(),
]);
export type OptomechViewShape = z.infer<typeof OptomechViewShapeSchema>;

/**
 * One lens or mount of the design as the 2D cross-section and the 3D viewers
 * draw it. `instance` is the body's name everywhere else — the composer's
 * instance for a catalog part, the importer's occurrence path for a file
 * body, the body id for a composed lens body — so the Mounts control, a run's
 * bodies and a binding name the same thing by the same key.
 */
export const OptomechViewBodySchema = z.object({
  instance: z.string().min(1),
  kind: z.enum(['lens', 'mount']),
  /** What the body is: the catalog part's name, the file body's name, the lens body's id. */
  name: z.string().min(1),
  /** Where it comes from, for the control's lighter line: "thorlabs SM1RR", the file's name, or empty. */
  subtitle: z.string(),
  /** The catalog family of a generated part; null for a file body and for a lens. */
  family: OptomechPartFamilySchema.nullable(),
  /** The lens face a placed ring bears on (OptomechPartSide); null for every other body. */
  side: OptomechPartSideSchema,
  shape: OptomechViewShapeSchema,
}).strict();
export type OptomechViewBody = z.infer<typeof OptomechViewBodySchema>;

/** Where the bodies come from: the composition that applies to the design (as laid out, built or not), or the design's own retained file. */
export const OptomechViewOriginSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('composition'),
    model_id: z.string().uuid(),
    /** The design version the stack was laid out around. */
    system_version: z.number().int().positive(),
    built: z.boolean(),
  }).strict(),
  z.object({ kind: z.literal('file'), file_name: z.string().min(1) }).strict(),
]);
export type OptomechViewOrigin = z.infer<typeof OptomechViewOriginSchema>;

/**
 * The bodies of the conversation's current design (or of one named
 * composition), one list whatever their origin: the composition that applies
 * to the current design when there is one (its parts and lenses from the
 * numbers that built them), else the retained file's lens and mount bodies as
 * meshes, else null. A viewer never reads an origin-specific route.
 *
 * `datum.front_surface` is the surface whose vertex is the frame's origin —
 * the first lens the composition or the import placed — so a viewer shifts
 * the list by where its own drawing puts that surface: the thicknesses from
 * surface 1 for optiland's layout and the CAD export, the engine's reported
 * placement for Zemax and CODE V.
 */
export const OptomechViewBodiesSchema = z.object({
  origin: OptomechViewOriginSchema,
  datum: OptomechDatumSchema,
  bodies: z.array(OptomechViewBodySchema),
}).strict();
export type OptomechViewBodies = z.infer<typeof OptomechViewBodiesSchema>;

/** Response of GET /optomech/view-bodies: null when the design has neither a composition nor a retained file (or on a draft). */
export const OptomechViewBodiesResponseSchema = z.object({ bodies: OptomechViewBodiesSchema.nullable() });
export type OptomechViewBodiesResponse = z.infer<typeof OptomechViewBodiesResponseSchema>;
